/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  Breadcrumbs,
  FormControl,
  Select,
  Tab,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import React, { useState, useMemo } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api/index.js";
import { MenuHeading } from "../../../components/menu_heading";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../user_management/styles";
import { FilterContainer } from "../../../components/filter_container";
import { selectBaseStyles, viewTypographyStyles } from "../styles";
import { StyledInputLabel } from "../../restaurant/styles.jsx";
import { UpdateButton } from "../../../components/update_button/index.jsx";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import DropdownSearchBar from "../../../components/dorpdown_searchbar/index.jsx";
import { ResetButton } from "../../../components/reset_button/index.jsx";
import { SelectedMenuItem } from '../../../components/selected_menu_item/index.jsx'
import ClearIcon from '@mui/icons-material/Clear';
import { StyledMenuItem } from "../../../components/styled_menu_item/index.jsx";
import { hasSingleItem } from "../../../utils/hasSingleItem.js";
import { getSingleItemValue } from "../../../utils/getSingleItemValue.js";
import { Query_Keys } from "../../../lib/queryKeys.js";
import { PerPage } from "../../../constants/perPage";
import { CACHE_DURATION } from "../../../constants/cache_duration";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";
import { ALL_VALUE, ReportMatrix } from "./report_matrix.jsx";

const datePickerStyles = {
  ".MuiInputBase-root": {
    height: "2.4rem",
    backgroundColor: "white",
    fontSize: "14px",
  },
  "& input::placeholder": {
    color: "black",
  },
  width: 160,
};

const RESTAURANT_TAB = 'restaurant'
const GROUP_TAB = 'group'
const RESTAURANT_REPORT = 'cross_restaurant_discounts'
const GROUP_REPORT = 'group_to_group_perqs'

const defaultFilter = {
  restaurant_group_id: ALL_VALUE,
  dateFilter: ALL_VALUE,
  restaurants: [ALL_VALUE],
  customDateFrom: null,
  customDateTo: null,
}

const formattedFilters = (sourceFilters, { includeRestaurants = true } = {}) => {
  const params = {};
  const today = dayjs();

  // Date range handling
  if (sourceFilters.dateFilter !== "Custom Date" && sourceFilters.dateFilter !== ALL_VALUE) {
    switch (sourceFilters.dateFilter) {
      case "Today":
        params.date_range = {
          from: today.format("DD/MM/YYYY"),
          to: today.format("DD/MM/YYYY"),
        };
        break;
      case "Last 7 Days":
        params.date_range = {
          from: today.subtract(7, "day").format("DD/MM/YYYY"),
          to: today.format("DD/MM/YYYY"),
        };
        break;
      case "Yesterday":
        params.date_range = {
          from: today.subtract(1, "day").format("DD/MM/YYYY"),
          to: today.subtract(1, "day").format("DD/MM/YYYY"),
        };
        break;
      case "This Week":
        params.date_range = {
          from: today.startOf("week").format("DD/MM/YYYY"),
          to: today.endOf("week").format("DD/MM/YYYY"),
        };
        break;
      case "This Month":
        params.date_range = {
          from: today.startOf("month").format("DD/MM/YYYY"),
          to: today.endOf("month").format("DD/MM/YYYY"),
        };
        break;
      case "Last Month":
        params.date_range = {
          from: today
            .subtract(1, "month")
            .startOf("month")
            .format("DD/MM/YYYY"),
          to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
        };
        break;
    }
  } else if (sourceFilters.dateFilter === "Custom Date" && sourceFilters.customDateFrom && sourceFilters.customDateTo) {
    params.date_range = {
      from: dayjs(sourceFilters.customDateFrom).format("DD/MM/YYYY"),
      to: dayjs(sourceFilters.customDateTo).format("DD/MM/YYYY"),
    };
  }

  if (sourceFilters?.restaurant_group_id !== ALL_VALUE)
    params.restaurant_group_ids = [sourceFilters?.restaurant_group_id];

  if (!includeRestaurants) return params;

  // Filter by id so restaurants sharing a name stay distinct.
  const restaurantIds = (sourceFilters?.restaurants || []).filter((id) => id && id !== ALL_VALUE);
  if (restaurantIds.length) params.restaurant_ids = restaurantIds;

  return params;
};

// Both reports return the same matrix; the row label comes back as `employee_from`.
// Entries, not object rest: the production build compiles rest into assignments, dropping a "__proto__" group.
const fetchReport = async (report, params) => {
  const response = await api.get(`/v1/admin/reports/${report}`, { params })
  return response?.data.map((record) => Object.fromEntries(Object.entries(record).map(
    ([key, value]) => [key === "employee_from" ? "from/To" : key, value],
  )))
}

export const DetailedTransaction = () => {
  const [activeTab, setActiveTab] = useState(RESTAURANT_TAB);
  const isGroupTab = activeTab === GROUP_TAB;
  const [filters, setFilters] = useState(defaultFilter);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [filtersChanged, setFiltersChanged] = useState(false);
  // Bumped by Update/Reset so the report tables go back to page 1.
  const [tableResetKey, setTableResetKey] = useState(0);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);

  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })
    return response?.data?.map(normalizeRestaurantGroup) || []
  }

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id,
      },
    });

    return response?.data?.map(normalizeRestaurant)?.filter((item) => item?.id && item?.name) || [];
  }

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  const restaurantReportParams = useMemo(() => formattedFilters(appliedFilters), [appliedFilters]);
  // The group report has no restaurant filter.
  const groupReportParams = useMemo(
    () => formattedFilters(appliedFilters, { includeRestaurants: false }),
    [appliedFilters],
  );

  const { data: restaurantReportData, isFetching: restaurantReportFetching } = useQuery({
    queryKey: Query_Keys.cross_restaurant_data({ filters: appliedFilters }),
    queryFn: () => fetchReport(RESTAURANT_REPORT, restaurantReportParams),
    staleTime: CACHE_DURATION,
    placeholderData: keepPreviousData,
    enabled: activeTab === RESTAURANT_TAB,
  })

  const { data: groupReportData, isFetching: groupReportFetching } = useQuery({
    // Keyed on what is sent: the Restaurant filter does not apply to this report.
    queryKey: Query_Keys.group_to_group_perqs(groupReportParams),
    queryFn: () => fetchReport(GROUP_REPORT, groupReportParams),
    staleTime: CACHE_DURATION,
    placeholderData: keepPreviousData,
    enabled: activeTab === GROUP_TAB,
  })

  const reportFetching = isGroupTab ? groupReportFetching : restaurantReportFetching;

  // Report rows/columns are keyed by name; the backend already scoped them by id.
  const appliedRestaurantNames = useMemo(() => {
    const selected = appliedFilters?.restaurants || [ALL_VALUE];
    if (selected.includes(ALL_VALUE)) return selected;

    return selected
      .map((id) => restaurantOptionsCache.find((restaurant) => restaurant?.id === id)?.name)
      .filter(Boolean);
  }, [appliedFilters, restaurantOptionsCache]);

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);

  React.useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      setFilters((prev) => ({
        ...prev,
        restaurants: [restaurants[0]?.id],
      }));
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  const handleRestaurantGroupChange = (event) => {
    setFilters({ ...filters, restaurant_group_id: event.target.value, restaurants: [ALL_VALUE] });
    setFiltersChanged(true);
  };

  const handleRestaurantGroupClear = () => {
    setFilters({ ...filters, restaurant_group_id: ALL_VALUE, restaurants: [ALL_VALUE] });
    setFiltersChanged(true);
  }

  const handleMultiSelectChange = (event) => {
    const {
      target: { value },
    } = event;

    setFiltersChanged(true);

    if ((value.length === 1 && value.includes(ALL_VALUE)) || value.length === 0) {
      setFilters({ ...filters, restaurants: [ALL_VALUE] });
    } else {
      const nextRestaurants = value.filter((val) => val !== ALL_VALUE);
      setFilters({ ...filters, restaurants: nextRestaurants });
    }
  }

  const handleRestaurantClear = () => {
    setFilters({ ...filters, restaurants: [ALL_VALUE] })
    setFiltersChanged(true)
  }

  const handleDateRangeClear = () => {
    setFilters({
      ...filters,
      dateFilter: ALL_VALUE,
      customDateFrom: null,
      customDateTo: null,
    });
    setFiltersChanged(true)
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      if (field === "dateFilter") {
        return {
          ...prev,
          dateFilter: value,
          customDateFrom: null,
          customDateTo: null,
        };
      }

      if (field === "customDateFrom") {
        if (!value) {
          return {
            ...prev,
            customDateFrom: null,
            customDateTo: null,
          };
        }

        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: value };
    });
    setFiltersChanged(true);
  };

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleUpdate = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setTableResetKey((key) => key + 1);
  };

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setFilters(defaultFilter)
    setTableResetKey((key) => key + 1);
  }

  const handleTabChange = (_event, nextTab) => setActiveTab(nextTab);

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurants || filters.restaurants.length === 0) {
      return 'All';
    }

    if (filters.restaurants.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters.restaurants.length === 1) {
      return restaurantOptionsCache.find((restaurant) => restaurant?.id === filters.restaurants[0])?.name || 'All';
    }

    return `${filters.restaurants.length} Restaurants Selected`;
  }

  const getRestaurantGroupName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup ? restaurantGroup?.value : 'All'
  }

  React.useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters(prev => ({
        ...prev,
        restaurant_group_id: singleRestaurantGroup.label,
        restaurants: [ALL_VALUE],
      }));
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  const Days = [
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ];

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Perq Reports</StyledLink>
            <StyledLink color={"#515152"}>
              Group / Restaurant Perqs
            </StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box 
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box 
              sx={{ 
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <Typography variant="body2" 
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap"
                }}
              >
                View
              </Typography>

              <FormControl 
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  value={hasSingleAvailableRestaurantGroup
                    ? getSingleItemValue(restaurantGroups)?.label
                    : (filters?.restaurant_group_id || ALL_VALUE)}
                  renderValue={() => {
                    if (hasSingleAvailableRestaurantGroup) {
                      return getSingleItemValue(restaurantGroups)?.value || 'All';
                    }
                    return getRestaurantGroupName(filters?.restaurant_group_id);
                  }}
                  label="Restaurant Group"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    width: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                  endAdornment= {
                    filters?.restaurant_group_id !== ALL_VALUE && !hasSingleAvailableRestaurantGroup ?(
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton 
                          edge='end' 
                          size="small" 
                          disableRipple
                          data-testid="restaurant-group-clear-btn"
                          onClick={handleRestaurantGroupClear}
                        >
                          <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                  {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                    value="All"
                  >
                    {filters?.restaurant_group_id === ALL_VALUE ? (
                      <SelectedMenuItem data={'All'} />
                    ) : (
                      'All'
                    )}
                  </StyledMenuItem>
                  )}
                  {
                    restaurantGroups
                      .map((res) => (
                        <StyledMenuItem
                          key={res?.label}
                          value={res?.label}
                        >
                          {hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value}
                        </StyledMenuItem>
                    ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl 
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "200px" }
                }}
              >
                <StyledInputLabel>Restaurant</StyledInputLabel>
                <Select
                  multiple
                  disabled={isGroupTab}
                  label="Restaurant"
                  value={hasSingleAvailableRestaurant
                    ? [getSingleItemValue(restaurants)?.id]
                    : (filters?.restaurants || [ALL_VALUE])}
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  renderValue={() => {
                    // The group report covers every restaurant, whatever the Restaurant tab selected.
                    if (isGroupTab) return 'All';
                    return hasSingleAvailableRestaurant
                      ? (getSingleItemValue(restaurants)?.name || 'All')
                      : getRestaurantDisplayValue()
                  }}
                  sx={{ 
                    ...selectBaseStyles, 
                    minWidth: { xs: "100%", sm: "200px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleMultiSelectChange}
                  onClose={() => setTimeout(() => setRestaurantSearch(""), 1000)}
                  endAdornment= {
                    !isGroupTab && !filters?.restaurants?.includes(ALL_VALUE) && !hasSingleAvailableRestaurant ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton 
                        edge='end' 
                        size="small" 
                        disableRipple
                        data-testid="restaurant-clear-btn"
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />

                  {!hasSingleAvailableRestaurant && (
                    <StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                      disabled={!filters?.restaurants?.includes(ALL_VALUE)}
                    >
                      {filters?.restaurants?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                  )}

                  {restaurants
                    ?.map((res) => (
                      <StyledMenuItem
                        key={res?.id}
                        value={res?.id}
                      >
                        {
                          hasSingleAvailableRestaurant || filters?.restaurants?.includes(res?.id) ? (
                            <SelectedMenuItem data={res?.name} />
                          ) : (
                            <>
                              {res?.name}
                            </>
                          )
                        }
                      </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl 
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Date Range</StyledInputLabel>
                <Select
                  value={filters?.dateFilter || ALL_VALUE}
                  renderValue={() => 
                    filters?.dateFilter
                  }
                  label="Date Range"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: '250px' } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={(e) =>
                    handleFilterChange("dateFilter", e.target.value)
                  }
                  endAdornment={
                    filters?.dateFilter !== ALL_VALUE? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          data-testid="date-range-clear-btn"
                          onClick={handleDateRangeClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  {/* All option */}
                  <StyledMenuItem
                    value={ALL_VALUE}
                  >
                    {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  {Days?.map((value) => (
                    <StyledMenuItem
                      key={value}
                      value={value}
                    >
                      {
                        filters?.dateFilter === value ? <SelectedMenuItem data={value} /> : value
                      }
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              {filters.dateFilter === "Custom Date" && (
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    width: { xs: "100%", sm: "auto" }
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      Days
                    </Typography>
                    <DatePicker
                      format="MM-DD-YYYY"
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: '160px' }
                      }}
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateFrom || null}
                      onChange={(date) =>
                        handleFilterChange("customDateFrom", date)
                      }
                    />
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      through
                    </Typography>
                    <DatePicker
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: '160px' }
                      }}
                      format="MM-DD-YYYY"
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateTo || null}
                      onChange={(date) =>
                        handleFilterChange("customDateTo", date)
                      }
                    />
                  </LocalizationProvider>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                status={reportFetching}
              />

              <UpdateButton
                onClick={handleUpdate}
                filtersChanged={filtersChanged}
                status={reportFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

        <MenuHeading report={false} dataTestId="perq-report-heading">Group / Restaurant Perqs</MenuHeading>

        <TabContext value={activeTab}>
          <Box>
            <TabList
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { backgroundColor: "#2b4fb9" } }}
              data-testid="perq-report-tabs"
            >
              <Tab label="Restaurant" value={RESTAURANT_TAB} sx={{ textTransform: "none" }} data-testid="restaurant-tab" />
              <Tab label="Group" value={GROUP_TAB} sx={{ textTransform: "none" }} data-testid="group-tab" />
            </TabList>
          </Box>

          <TabPanel value={RESTAURANT_TAB} data-testid="restaurant-panel" sx={{ px: 0, pb: 0, pt: 2 }}>
            <ReportMatrix
              data={restaurantReportData}
              isFetching={restaurantReportFetching}
              resetKey={tableResetKey}
              report={RESTAURANT_REPORT}
              params={restaurantReportParams}
              storageKey="tableWidths-detailedTransaction"
              selectedNames={appliedRestaurantNames}
            />
          </TabPanel>

          <TabPanel value={GROUP_TAB} data-testid="group-panel" sx={{ px: 0, pb: 0, pt: 2 }}>
            <ReportMatrix
              data={groupReportData}
              isFetching={groupReportFetching}
              resetKey={tableResetKey}
              report={GROUP_REPORT}
              params={groupReportParams}
              storageKey="tableWidths-detailedTransaction-group"
            />
          </TabPanel>
        </TabContext>
      </Box>
    </React.Fragment>
  );
};
